import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

interface ScaledProps {
  /** The figure's own size, in CSS pixels, as it was drawn. */
  readonly width: number;
  readonly height: number;
  readonly children: ReactNode;
}

/**
 * A fixed-size figure shown at whatever width it is given. Below its own
 * width it is scaled down as one piece — the SVG, its annotations, the lot —
 * rather than reflowed, so a build diagram drawn for 640 px reads the same
 * on a phone, only smaller. Above its own width it stays at 1:1.
 */
export function Scaled({ width, height, children }: ScaledProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / width));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div ref={ref} style={{ width: "100%", height: height * scale, overflow: "hidden" }}>
      <div style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left" }}>{children}</div>
    </div>
  );
}
